import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Container,
  Header,
  Input,
  SpaceBetween,
  Table,
  TokenGroup,
} from '@amzn/awsui-components-react';
import type { IndustryGrants } from '../../client/IvtHubClient';
import { deleteIndustryGrant, listIndustryGrants } from '../../client/industryAdminClient';
import { denialHint } from '../../utils/authorization';
import { formatTimestamp, lastGrant } from './industryAccess';
import { GrantAccessModal } from './GrantAccessModal';

/** Principals shown per row before "Show more (+N)". */
const COLLAPSED_LIMIT = 4;

/**
 * Who can see each industry. Every change is saved by its own call and is not part of the
 * page's Publish: grants carry no sync-scope revision, so unsaved Scope / Industry mapping
 * edits neither block them nor are touched by them.
 */
export function AccessTab() {
  const [rows, setRows] = useState<IndustryGrants[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [removing, setRemoving] = useState<Set<string>>(new Set());
  const [granting, setGranting] = useState<string[] | null>(null);

  // Bumped by every load and every local edit; a list response is applied only while its
  // number is still current, so a stale one cannot bring back a just-removed chip.
  const seq = useRef(0);
  const loading = useRef(false);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    loading.current = true;
    setLoadError(null);
    try {
      const list = await listIndustryGrants();
      if (mine === seq.current) setRows(list);
    } catch (e) {
      if (mine === seq.current) setLoadError(denialHint(e, 'en') ?? 'Could not load access.');
    } finally {
      if (mine === seq.current) loading.current = false;
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const remove = async (industry: string, alias: string) => {
    const key = `${industry}|${alias}`;
    setActionError(null);
    setRemoving((s) => new Set(s).add(key));
    try {
      await deleteIndustryGrant(industry, alias);
      // Drop any list already in flight; it may predate the delete. Re-fetch in its place.
      const dropped = loading.current;
      seq.current += 1;
      loading.current = false;
      if (dropped) void load();
      setRows((prev) =>
        (prev ?? []).map((r) =>
          r.industry === industry
            ? { ...r, grantees: (r.grantees ?? []).filter((g) => g.alias !== alias) }
            : r,
        ),
      );
    } catch (e) {
      setActionError(
        `Could not remove ${alias} from ${industry}: ${denialHint(e, 'en') ?? (e as Error)?.message ?? 'unknown error'}`,
      );
    } finally {
      setRemoving((s) => {
        const next = new Set(s);
        next.delete(key);
        return next;
      });
    }
  };

  const q = query.trim().toLowerCase();
  const all = rows ?? [];
  const aliasHit = (a: string | undefined) => !!q && !!a?.includes(q);
  const visible = q
    ? all.filter(
        (r) =>
          (r.industry ?? '').toLowerCase().includes(q) ||
          (r.grantees ?? []).some((g) => aliasHit(g.alias)),
      )
    : all;
  // An exact alias reads as "this person", so say which industries they can see.
  const personIndustries = q ? all.filter((r) => (r.grantees ?? []).some((g) => g.alias === q)) : [];
  const isPerson = personIndustries.length > 0;

  return (
    <Container
      header={
        <Header
          variant="h2"
          actions={
            <Button disabled={!rows} onClick={() => setGranting([])}>
              Grant access
            </Button>
          }
          description="Who can see each industry. A change applies immediately; it is not part of Publish."
        >
          Access
        </Header>
      }
    >
      <SpaceBetween size="m">
        {loadError && (
          <Alert type="error" header="Could not load" action={<Button onClick={() => void load()}>Retry</Button>}>
            {loadError}
          </Alert>
        )}
        {actionError && (
          <Alert type="error" dismissible onDismiss={() => setActionError(null)}>
            {actionError}
          </Alert>
        )}
        <Input
          type="search"
          value={query}
          onChange={({ detail }) => setQuery(detail.value)}
          placeholder="Find by person or industry"
          ariaLabel="Find by person or industry"
        />
        {isPerson && (
          <SpaceBetween direction="horizontal" size="xs" alignItems="center">
            <Box variant="strong">{q} can see:</Box>
            {personIndustries.map((r) => (
              <Badge key={r.industry} color="blue">
                {r.industry}
              </Badge>
            ))}
          </SpaceBetween>
        )}
        <Table
          variant="embedded"
          loading={!rows && !loadError}
          loadingText="Loading access"
          items={visible}
          trackBy="industry"
          // Not "No industries" when the load failed: the Alert above says what happened.
          empty={loadError ? null : <Box textAlign="center">{q ? 'No match' : 'No industries'}</Box>}
          columnDefinitions={[
            {
              id: 'principals',
              header: 'Principals',
              cell: (r) => {
                const industry = r.industry ?? '';
                // A match is listed first and the row expanded, so the hit is never behind "+N".
                const grantees = [...(r.grantees ?? [])].sort(
                  (a, b) => Number(aliasHit(b.alias)) - Number(aliasHit(a.alias)),
                );
                return (
                  <SpaceBetween size="xxs">
                    {grantees.length > 0 && (
                      <TokenGroup
                        limit={q ? undefined : COLLAPSED_LIMIT}
                        // Renders as "Show more (+N)".
                        i18nStrings={{ limitShowMore: 'Show more', limitShowFewer: 'Show fewer' }}
                        items={grantees.map((g) => ({
                          label: g.alias ?? '',
                          dismissLabel: `Remove ${g.alias}`,
                          disabled: removing.has(`${industry}|${g.alias}`),
                          iconName: aliasHit(g.alias) ? ('search' as const) : undefined,
                        }))}
                        onDismiss={({ detail }) => {
                          const alias = grantees[detail.itemIndex]?.alias;
                          if (alias) void remove(industry, alias);
                        }}
                      />
                    )}
                    <Button variant="inline-link" iconName="add-plus" onClick={() => setGranting([industry])}>
                      Add
                    </Button>
                  </SpaceBetween>
                );
              },
            },
            {
              id: 'industry',
              header: 'Industry',
              cell: (r) => <Box variant="strong">{r.industry}</Box>,
            },
            {
              id: 'changed',
              header: 'Last changed',
              cell: (r) => {
                const g = lastGrant(r.grantees);
                return g ? `${g.grantedBy} · ${formatTimestamp(g.grantedAt)}` : '—';
              },
            },
          ]}
        />
      </SpaceBetween>

      {granting && (
        <GrantAccessModal
          industries={all.map((r) => r.industry ?? '')}
          initialIndustries={granting}
          onDismiss={() => setGranting(null)}
          onChanged={() => void load()}
        />
      )}
    </Container>
  );
}
